import { useCallback, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import type { StorageError, TransformOptions } from "@supabase/storage-js";

import apiClient from "@/api/apiClient";
import { notify } from "@/hooks/useNotifications";
import i18n from "@/providers/i18n";
import { getDateNow } from "@/utils/dates";
import { getCachedSignedFileUrl, setCachedSignedFileUrl } from "@/utils/localStorageCache";
import { fileExtensionByMime, isImageFile } from "@/utils/media";

export const THUMBNAIL_IDENTIFIER = "_THUMB";
export const MAX_UPLOAD_FILE_SIZE = 50 * 1024 ** 2; // 50 Megabytes
export const SIGNED_URL_EXPIRATION_MS = 86400000; // 1 day expiration
export const SIGNED_URL_EXPIRATION_SEC = SIGNED_URL_EXPIRATION_MS / 1000; // 1 day expiration
export const THUMBNAIL_SETTINGS: TransformOptions = {
  width: 150, // in px
  resize: "contain", // preserve aspect ratio, while resizing by width
  quality: 50, // in %
};

export interface SignedFileSrc {
  src?: string;
  thumbnail?: string;
}

export type FileMetaInformation = Pick<File, "name" | "size" | "type"> & {
  id: string;
  path: string;
  bucket_id: string;
  description?: string;
  title?: string;
  ordering?: number;
  updated_at: string;
  created_at: string;
};

export interface BucketFile {
  updated_at: string;
  created_at: string;
  id: string;
  path: string;
  bucket_id: string;
  description?: string;
  title?: string;
  ordering?: number;
}

export type SignedFileUrl = BucketFile & SignedFileSrc;

export type SignedFleUrlStorageResponse = Promise<
  | {
      data: {
        signedUrl: string;
      };
      error: null;
    }
  | {
      data: null;
      error: StorageError;
    }
>;

export const uploadFiles = async (files: File[], bucket_id: string, workspace_id?: string) => {
  if (files.length < 1 || !bucket_id) return;

  const uploadedFiles: FileMetaInformation[] = [];

  try {
    for (const file of files) {
      const id = uuidv4();
      const extension = fileExtensionByMime[file.type];

      if (!extension) return;
      const folder = workspace_id ? `${workspace_id}/` : "";
      const path = `${folder}${id}${extension}`;

      const { data, error } = await apiClient.storage.from(bucket_id).upload(path, file);
      if (error) {
        console.error(error);
        notify.error("Error uploading some images. Please try again.");
      }

      if (data) {
        const { name, type, size } = file;
        uploadedFiles.push({
          id,
          bucket_id,
          name,
          type,
          size,
          path,
          updated_at: getDateNow(),
          created_at: getDateNow(),
        });
      }
    }

    return uploadedFiles;
  } catch (error) {
    console.error(error);
    notify.error("Image upload failed. Please try again.");
  }
};

export const deleteFiles = async (names: string[], bucket: string) => {
  try {
    const { data, error: storageError } = await apiClient.storage.from(bucket).remove(names);
    if (storageError) throw storageError;
    return data;
  } catch (error) {
    console.error(error);
    notify.error(i18n.t("notify.delete.error", { e: i18n.t("files") }));
  }
};

interface CreateSignedUrlProps {
  file?: BucketFile | null;
  asThumbnail?: boolean;
}
export const createSignedUrl = async ({
  file,
  asThumbnail,
}: CreateSignedUrlProps): Promise<SignedFileUrl | undefined> => {
  if (!file) return;
  const identifier = asThumbnail ? THUMBNAIL_IDENTIFIER : undefined;
  let cachedUrl = getCachedSignedFileUrl({ file, identifier });

  if (!cachedUrl) {
    const { data } = await apiClient.storage
      .from(file.bucket_id)
      .createSignedUrl(file.path, SIGNED_URL_EXPIRATION_SEC, {
        transform: asThumbnail ? THUMBNAIL_SETTINGS : undefined,
      });
    if (data) {
      cachedUrl = data.signedUrl;

      setCachedSignedFileUrl({
        file,
        identifier,
        url: data.signedUrl,
        expiration: SIGNED_URL_EXPIRATION_MS,
      });
    }
  }
  const { updated_at, created_at, id, path, bucket_id, title, description, ordering } = file;
  return {
    updated_at,
    created_at,
    id,
    path,
    bucket_id,
    title,
    description,
    ordering,
    src: cachedUrl ?? undefined,
  };
};

export interface CreateSignedUrlsFromFilesProps {
  files?: (BucketFile | FileMetaInformation)[];
  withSrc?: boolean;
  withThumbnails?: boolean;
}
export const createSignedUrlsFromFiles = async ({
  files,
  withSrc = true,
  withThumbnails = true,
}: CreateSignedUrlsFromFilesProps): Promise<SignedFileUrl[]> => {
  if (!files || files.length < 1) return [];

  const urlRequestsSrc: (SignedFleUrlStorageResponse | string | null)[] = [];
  const urlRequestsThumbnail: (SignedFleUrlStorageResponse | string | null)[] = [];

  files.forEach((file) => {
    if (withSrc) {
      const cachedUrl = getCachedSignedFileUrl({ file });
      urlRequestsSrc.push(
        cachedUrl
          ? cachedUrl
          : apiClient.storage.from(file.bucket_id).createSignedUrl(file.path, SIGNED_URL_EXPIRATION_SEC),
      );
    } else {
      urlRequestsSrc.push(null);
    }

    if (withThumbnails && isImageFile(file.path)) {
      const cachedUrl = getCachedSignedFileUrl({ file, identifier: THUMBNAIL_IDENTIFIER });
      urlRequestsThumbnail.push(
        cachedUrl
          ? cachedUrl
          : apiClient.storage.from(file.bucket_id).createSignedUrl(file.path, SIGNED_URL_EXPIRATION_SEC, {
              transform: THUMBNAIL_SETTINGS,
            }),
      );
    } else {
      urlRequestsThumbnail.push(null);
    }
  });

  try {
    const urlResponsesThumbnail = await Promise.all(urlRequestsThumbnail);
    const urlResponsesSrc = await Promise.all(urlRequestsSrc);

    const fileUrls: SignedFileUrl[] = [];

    for (let i = 0; i < urlResponsesSrc.length; i++) {
      const { updated_at, created_at, id, path, bucket_id, title, description, ordering } = files[i];
      const urlReponseSrc = urlResponsesSrc[i];
      const urlResponseThumbnail = urlResponsesThumbnail[i];

      let _src: string | undefined = undefined;
      let _thumbnail: string | undefined = undefined;
      if (typeof urlReponseSrc === "string") {
        _src = urlReponseSrc;
      } else if (urlReponseSrc && !urlReponseSrc.error) {
        _src = urlReponseSrc.data.signedUrl;
        setCachedSignedFileUrl({
          file: files[i],
          url: urlReponseSrc.data.signedUrl,
          expiration: SIGNED_URL_EXPIRATION_MS,
        });
      }

      if (typeof urlResponseThumbnail === "string") {
        _thumbnail = urlResponseThumbnail;
      } else if (urlResponseThumbnail && !urlResponseThumbnail.error) {
        _thumbnail = urlResponseThumbnail.data.signedUrl;
        setCachedSignedFileUrl({
          file: files[i],
          identifier: THUMBNAIL_IDENTIFIER,
          url: urlResponseThumbnail.data.signedUrl,
          expiration: SIGNED_URL_EXPIRATION_MS,
        });
      }

      fileUrls.push({
        updated_at,
        created_at,
        id,
        path,
        bucket_id,
        title,
        description,
        ordering,
        src: _src,
        thumbnail: _thumbnail,
      });
    }

    return fileUrls;
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const downloadFile = async (fileName: string, bucket: string) => {
  try {
    const { data, error } = await apiClient.storage.from(bucket).download(fileName);
    if (error) throw error;
    return data;
  } catch (error) {
    console.error(error);
    notify.error("Error downloading file. Please try again.");
  }
};

export const saveFileToDisk = async (fileName: string, bucket: string) => {
  const blob = await downloadFile(fileName, bucket);
  if (!blob) return;

  const a = document.createElement("a");
  document.body.appendChild(a);
  const url = URL.createObjectURL(blob);
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, 0);
};

export const useGetSignedUrl = ({ file, asThumbnail }: CreateSignedUrlProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [signedFileUrl, setSignedFileUrl] = useState<SignedFileUrl | undefined>(undefined);

  const _convertFileToUrl = useCallback(
    async (fileUrl: SignedFileUrl) => {
      setIsLoading(true);
      const file = await createSignedUrl({ file: fileUrl, asThumbnail });
      setSignedFileUrl(file);
      setIsLoading(false);
    },
    [asThumbnail],
  );

  useEffect(() => {
    if (file) void _convertFileToUrl(file);
  }, [_convertFileToUrl, file]);

  return {
    isLoading,
    signedFileUrl,
  };
};

export const useGetSignedUrls = ({ files, withSrc, withThumbnails }: CreateSignedUrlsFromFilesProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [signedFileUrls, setSignedFileUrls] = useState<SignedFileUrl[]>([]);

  const _convertFilesToUrls = useCallback(
    async (_fileUrls: SignedFileUrl[]) => {
      setIsLoading(true);
      const files = await createSignedUrlsFromFiles({ files: _fileUrls, withSrc, withThumbnails });
      setSignedFileUrls(files);
      setIsLoading(false);
    },
    [withSrc, withThumbnails],
  );

  useEffect(() => {
    if (files) void _convertFilesToUrls(files);
  }, [_convertFilesToUrls, files, withSrc, withThumbnails]);

  return {
    isLoading,
    signedFileUrls,
  };
};

// TODO: deletefiles isn't used anywhere -> integrate with respective delete hooks
export const useSupabaseStorage = () => {
  return {
    uploadFiles,
    deleteFiles,
    createSignedUrl,
    createSignedUrlsFromFiles,
    downloadFile,
    saveFileToDisk,
  };
};
